import type { CSSProperties, ComponentPropsWithRef, ReactNode } from "react";
import { forwardRef } from "react";
import { isEmpty, omit } from "lodash";
import { classNames } from "@/react/helpers/twMergeWithCN";
import { getBlocksFromChildren } from "@circle-react-shared/BuilderUI/Helpers/blockUtils";
import type { StyleValue } from "@circle-react-shared/BuilderUI/StyleValue.types";
import { parseStyles } from "@circle-react-shared/BuilderUI/utils/parseStyles";
import { defaultStyles } from "./defaultStyles";

interface ContainerProps extends Omit<ComponentPropsWithRef<"div">, "style"> {
  children: ReactNode;
  styles?: Readonly<Record<string, StyleValue>>;
}

const getContainerClass = (containerValue: string) => {
  switch (containerValue) {
    case "640px":
      return "wb-container wb-max-w-screen-sm";
    case "768px":
      return "wb-container wb-max-w-screen-md";
    case "1024px":
      return "wb-container wb-max-w-screen-lg";
    case "1280px":
      return "wb-container wb-max-w-screen-xl";
    case "1536px":
      return "wb-container wb-max-w-screen-2xl";
    case "none":
      return "wb-w-full";
    default:
      return "wb-container";
  }
};

export const Container = forwardRef<HTMLDivElement, ContainerProps>(
  ({ children, styles = {}, className, ...rest }, ref) => {
    const container =
      String(styles.container?.value) || defaultStyles.container;

    const inlineStyles: CSSProperties = {
      ...parseStyles({
        styles: omit(styles, "container"),
        defaultStyles,
      }),
    };

    const childrenBlocks = getBlocksFromChildren(children);

    return (
      <div
        ref={ref}
        className={classNames(
          getContainerClass(container),
          "wb-mx-auto wb-p-8 wb-h-auto wb-min-h-16",
          {
            "wb-border-gray-500 wb-border wb-border-dashed wb-shadow-[inset_0_0_2px_4px_rgba(0,0,0,0.1),inset_0_0_2px_4px_rgba(255,255,255,0.2),inset_0_0_3px_rgba(255,255,255,0.3)]":
              isEmpty(childrenBlocks),
          },
          className,
        )}
        style={inlineStyles}
        {...rest}
      >
        {children}
      </div>
    );
  },
);

Container.displayName = "Container";
